import type { IntegrationKind, UserOrganization } from '@outpost/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Webhook } from 'lucide-react';
import { useState } from 'react';

import { ErrorState, ListSkeleton } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import { allows } from '@/lib/roles';

/**
 * Where alerts can be delivered, for every project in the organization.
 *
 * Only webhooks and Slack appear here. Email is not an integration: an email
 * action carries its own recipients, and the SMTP server is a property of the
 * deployment rather than of an organization.
 */
export function Integrations({ organization }: { organization: UserOrganization }) {
  const { client } = useAuth();
  const canEdit = allows(organization.role, 'admin');

  const integrations = useQuery({
    queryKey: ['integrations', organization.id],
    queryFn: () => client.listIntegrations(organization.id),
  });

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-base">
            <Webhook className="size-4" />
            Notification integrations
          </CardTitle>
          <CardDescription>
            Alert rules in any project can send to these. The URL is a credential — it is stored
            once here and never shown again.
          </CardDescription>
        </div>
        {canEdit && <IntegrationDialog organization={organization} />}
      </CardHeader>
      <CardContent className="space-y-3">
        {integrations.isLoading && <ListSkeleton rows={2} />}
        {integrations.isError && (
          <ErrorState error={integrations.error} onRetry={() => void integrations.refetch()} />
        )}

        {integrations.isSuccess && integrations.data.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No integrations yet. Alert rules can still send email without one.
          </p>
        )}

        {integrations.data?.map((integration) => (
          <div
            key={integration.id}
            className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{integration.name}</span>
            <Badge variant="secondary">{integration.kind}</Badge>
            {!integration.is_active && <Badge variant="muted">disabled</Badge>}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function IntegrationDialog({ organization }: { organization: UserOrganization }) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<IntegrationKind>('slack');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');

  const create = useMutation({
    mutationFn: () =>
      client.createIntegration(organization.id, {
        kind,
        name: name.trim(),
        // The key differs per kind because that is what the worker reads when
        // it builds the notifier; the wrong one would fail only at alert time.
        config:
          kind === 'slack'
            ? { webhook_url: url.trim() }
            : secret.trim()
              ? { url: url.trim(), secret: secret.trim() }
              : { url: url.trim() },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['integrations', organization.id] });
      setOpen(false);
      setName('');
      setUrl('');
      setSecret('');
    },
  });

  const usable = name.trim() !== '' && /^https?:\/\/\S+$/.test(url.trim());

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus />
          Add integration
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add an integration</DialogTitle>
          <DialogDescription>
            Alert rules refer to it by name, so name it after where it posts rather than after the
            rule that uses it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="integration-kind">Kind</Label>
          <Select value={kind} onValueChange={(value) => setKind(value as IntegrationKind)}>
            <SelectTrigger id="integration-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="slack">Slack</SelectItem>
              <SelectItem value="webhook">Webhook</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="integration-name">Name</Label>
          <Input
            id="integration-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={kind === 'slack' ? '#alerts' : 'PagerDuty'}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="integration-url">
            {kind === 'slack' ? 'Slack incoming webhook URL' : 'Endpoint URL'}
          </Label>
          <Input
            id="integration-url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder={
              kind === 'slack'
                ? 'https://hooks.slack.com/services/…'
                : 'https://example.com/hooks/outpost'
            }
          />
        </div>

        {kind === 'webhook' && (
          <div className="space-y-1.5">
            <Label htmlFor="integration-secret">Signing secret (optional)</Label>
            <Input
              id="integration-secret"
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              placeholder="Used to sign X-Outpost-Signature"
            />
            <p className="text-xs text-muted-foreground">
              With a secret, every delivery carries an HMAC-SHA256 of the body so the receiver can
              tell a real notification from anyone who learned the URL.
            </p>
          </div>
        )}

        {create.error && (
          <p className="text-sm text-destructive">{(create.error as Error).message}</p>
        )}

        <DialogFooter>
          <Button onClick={() => create.mutate()} disabled={!usable || create.isPending}>
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
